/**
 * AI CONTEXT: mobile-first - Checkout page with payment processing
 * Journal: J-20250812-01
 * 
 * This component implements the checkout process with:
 * - Mobile-first responsive design
 * - Payment method selection
 * - Order summary and credit calculation
 * - Subscription upsell functionality
 * - Secure payment processing
 * 
 * Architecture decisions:
 * - Use glass morphism effects for modern UI
 * - Implement dark theme for consistency
 * - Provide clear order summary and credit information
 * - Include subscription upsell for revenue optimization
 * 
 * Future considerations:
 * - Add real payment processing integration
 * - Implement order confirmation emails
 * - Add inventory management
 * - Consider multi-step checkout flow
 */

import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { createCheckoutSession, verifyCheckoutSession, isApiConfigured, validateCode, getMyProfile, updateMyProfile } from '../services/apiClient';
import { useNavigate, Link } from 'react-router-dom';
import S3Image from '../components/S3Image';
import BurstBags from '../components/BurstBags';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useProducts } from '../contexts/ProductContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useGiveaway } from '../contexts/GiveawayContext';
import { 
  ShoppingCart, 
  CreditCard, 
  Gift, 
  Crown,
  CheckCircle,
  Lock,
  Plus,
  Minus,
  MessageSquare,
  Shield,
  AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { creditService } from '../services/creditService';
import { t } from '../i18n';
// import AgeVerificationModal from '../components/AgeVerificationModal'; // DISABLED - Stripe verification
import { calculateTax, getTaxRatePercentage } from '../utils/taxCalculator';
import { setOrCreateMeta } from '../utils/seo';
import { handleCheckoutSmsConsent, getUserPhoneE164 } from '../services/smsService';
import { readPendingGiveawayEntry } from '../utils/pendingGiveawayEntry';

const Checkout = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, addPurchase } = useAuth();
  const { cart, getCartTotal, getCartCount, getTotalEntries, checkout, clearCart, clearCartAfterPurchase, restoreCartFromStorage, updateQuantity, syncCartWithProducts } = useCart();
  const { products: apiProducts } = useProducts();
  const { createSubscription, isUserSubscribed } = useSubscription();
  const { addCredits } = useGiveaway();
  
  const apiConfigured = isApiConfigured();
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [showSubscriptionUpsell, setShowSubscriptionUpsell] = useState(false);
  const [showBonusEntries, setShowBonusEntries] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [appliedReferral, setAppliedReferral] = useState(null);
  // const [showAgeVerification, setShowAgeVerification] = useState(false); // DISABLED - Stripe verification
  const [autoProceeding, setAutoProceeding] = useState(false);
  // const [returningFromVerification, setReturningFromVerification] = useState(false); // DISABLED - Stripe verification
  const [ambientTick, setAmbientTick] = useState(0);
  const [showSimpleAgeConfirm, setShowSimpleAgeConfirm] = useState(false); // Simple age confirmation
  const [formData, setFormData] = useState({
    smsConsent: false
  });
  const [hasSmsConsent, setHasSmsConsent] = useState(false); // Track if user already has SMS consent
  const [loadingConsent, setLoadingConsent] = useState(true); // Track loading state for consent check
  
  // Check if user just completed verification (handled inline where needed)

  // AI CONTEXT: sales-pricing - Helper to get effective price (sales price if available, otherwise regular price)
  // Treat salesPrice of 0 as null/undefined (no discount) to avoid $0 checkout bug
  const getEffectivePrice = (item) => {
    if (item.salesPrice !== null && item.salesPrice !== undefined && item.salesPrice > 0) {
      return item.salesPrice;
    }
    return item.price;
  };

  // AI CONTEXT: payments - Subscription detection and correction guardrail
  // Journal: J-20260112-01 (Enhanced)
  // Reasoning: Automatically detect subscription items and ensure they have proper flags
  // to prevent them from being processed as one-time payments in Stripe
  const detectAndCorrectSubscriptionItem = (item) => {
    // Keywords that indicate a subscription product
    const subscriptionKeywords = ['subscription', 'premium', 'monthly', 'yearly', 'annual'];
    const itemName = (item.name || '').toLowerCase();
    const itemId = (item.id || '').toLowerCase();
    
    // Check if the item name OR id contains subscription keywords
    const isLikelySubscription = subscriptionKeywords.some(keyword => 
      itemName.includes(keyword) || itemId.includes(keyword)
    );
    
    // Additional check: if price is exactly $24.99 and tier is Bronze, it's likely the subscription
    const isBronzeSubscriptionPrice = item.price === 24.99 && item.tier === 'Bronze';
    
    // If item looks like a subscription but doesn't have the flag, correct it
    if ((isLikelySubscription || isBronzeSubscriptionPrice) && !item.isSubscription) {
      console.warn(`⚠️ SUBSCRIPTION GUARDRAIL: Detected potential subscription item "${item.name}" without isSubscription flag. Auto-correcting...`);
      console.warn(`   Price: $${item.price}, Tier: ${item.tier}, Keywords match: ${isLikelySubscription}`);
      return {
        ...item,
        isSubscription: true,
        interval: item.interval || 'month' // Default to monthly if no interval specified
      };
    }
    
    // If item has isSubscription flag, ensure it has interval
    if (item.isSubscription && !item.interval) {
      console.warn(`⚠️ SUBSCRIPTION GUARDRAIL: Subscription item "${item.name}" missing interval. Setting to 'month'.`);
      return {
        ...item,
        interval: 'month'
      };
    }
    
    return item;
  };

  // Merchant Platform Fee (MPF): 5% of cart amount before tax (after discounts),
  // rounded UP to the nearest whole dollar if any cents are present.
  function computeMerchantPlatformFee() {
    const subtotal = computeSubtotal();
    const discount = computeDiscountAmount();
    const base = Math.max(0, subtotal - discount);
    const raw = base * 0.05;
    const dollars = Math.floor(raw);
    const cents = +(raw - dollars).toFixed(2);
    if (cents > 0) {
      return dollars + 1;
    }
    return dollars;
  }

  // DEBUG: Test environment variable reading
  useEffect(() => {
    console.log('🔍 DEBUG: Checkout component mounted');
    console.log('🔍 DEBUG: REACT_APP_AGE_VERIFICATION_ENABLED =', process.env.REACT_APP_AGE_VERIFICATION_ENABLED);
    console.log('🔍 DEBUG: REACT_APP_AGE_VERIFICATION_ENABLED type =', typeof process.env.REACT_APP_AGE_VERIFICATION_ENABLED);
    console.log('🔍 DEBUG: REACT_APP_AGE_VERIFICATION_ENABLED === "true" =', process.env.REACT_APP_AGE_VERIFICATION_ENABLED === 'true');
  }, []);

  useEffect(() => {
    setOrCreateMeta('meta[name="robots"]', { name: 'robots' }, 'content', 'noindex,nofollow');
  }, []);

  // AI CONTEXT: ux - Load user consent status on mount
  // Reasoning: Only show consent checkboxes if user hasn't already consented
  // This prevents showing consent forms on every checkout after initial consent
  useEffect(() => {
    const loadConsentStatus = async () => {
      if (!isAuthenticated || !user) {
        setLoadingConsent(false);
        return;
      }

      try {
        setLoadingConsent(true);

        // Check SMS consent from user profile
        try {
          const profile = await getMyProfile();
          const smsConsented = profile?.smsConsent === true;
          setHasSmsConsent(smsConsented);
        } catch (error) {
          console.warn('Failed to load SMS consent status:', error);
          setHasSmsConsent(false); // Default to showing checkbox if we can't determine status
        }

        // Push notifications disabled - no need to check
      } catch (error) {
        console.error('Error loading consent status:', error);
        // On error, default to showing checkboxes (safer for compliance)
        setHasSmsConsent(false);
      } finally {
        setLoadingConsent(false);
      }
    };

    loadConsentStatus();
  }, [isAuthenticated, user]);

  // AI CONTEXT: sales-pricing - Sync cart with current product data on checkout page load
  // Reasoning: Ensure cart items have current prices and sales prices, even if added before sale started
  // This intelligently corrects the cart when items go on sale
  useEffect(() => {
    if (apiProducts && apiProducts.length > 0 && cart && cart.length > 0) {
      syncCartWithProducts(apiProducts);
    }
  }, [apiProducts, syncCartWithProducts]); // Note: cart dependency intentionally omitted to avoid loops
  
  // Define handleCheckout before early returns to avoid React Hook rules violation
          const handleCheckout = useCallback(async (forceCart = null) => {
          setLoading(true);
          setAutoProceeding(false); // Reset auto-proceeding state
          
          try {
            // AI CONTEXT: age-verification - Simple age confirmation (Stripe verification disabled)
            // Reasoning: Simplified age verification with just a confirmation prompt
            // The Stripe Identity verification code is preserved below (commented) for future re-enablement
            
            // Check simple age confirmation from localStorage
            const simpleAgeConfirmed = localStorage.getItem('tolbert_simple_age_confirmed');
            
            if (simpleAgeConfirmed !== 'true') {
              console.log('❌ User has not confirmed age - showing simple confirmation');
              setShowSimpleAgeConfirm(true);
              setLoading(false);
              return;
            }
            
            console.log('✅ User has confirmed age - proceeding with checkout');
            
            /* 
            // ===== STRIPE AGE VERIFICATION (DISABLED) =====
            // The code below implements Stripe Identity verification and is preserved for future use.
            // To re-enable, uncomment this section and remove the simple age confirmation above.
            
            // Check age verification status first (skip if returning from verification)
            console.log('🔍 DEBUG: handleCheckout started');
            console.log('🔍 DEBUG: returningFromVerification flag:', returningFromVerification);
            console.log('🔍 DEBUG: AGE_VERIFICATION_ENABLED:', process.env.REACT_APP_AGE_VERIFICATION_ENABLED);
            console.log('🔍 DEBUG: AGE_VERIFICATION_ENABLED type:', typeof process.env.REACT_APP_AGE_VERIFICATION_ENABLED);
            console.log('🔍 DEBUG: AGE_VERIFICATION_ENABLED === "true":', process.env.REACT_APP_AGE_VERIFICATION_ENABLED === 'true');
            
            // Optimized age verification: localStorage first, database fallback
            const urlParams = new URLSearchParams(window.location.search);
            const verificationParam = urlParams.get('verification');
            const localStorageVerification = localStorage.getItem('tolbert_age_verification_complete');
            const isReturningFromVerification = verificationParam === 'complete';
            
            console.log('🔍 DEBUG: localStorage verification:', localStorageVerification);
            console.log('🔍 DEBUG: URL verification param:', verificationParam);
            console.log('🔍 DEBUG: isReturningFromVerification:', isReturningFromVerification);
            
            // Step 1: Check localStorage first (fast, no API call)
            if (localStorageVerification === 'true') {
              console.log('✅ Age verification found in localStorage - skipping database check');
              // User is verified in localStorage, proceed with checkout
            } 
            // Step 2: Check if returning from verification (URL parameter)
            else if (isReturningFromVerification) {
              console.log('✅ Returning from age verification - bypassing verification check and proceeding to payment');
            }
            // Step 3: Fallback to database check (only if localStorage is false/null)
            else if (process.env.REACT_APP_AGE_VERIFICATION_ENABLED === 'true' || typeof process.env.REACT_APP_AGE_VERIFICATION_ENABLED === 'undefined') {
              console.log('🔍 DEBUG: Age verification assumed enabled, checking database...');
              try {
                console.log('🔍 localStorage verification not found - checking database...');
                const verificationStatus = await authorizedFetch('/verification/status', { method: 'GET' });
                console.log('🔍 DEBUG: Database verification status:', verificationStatus);
                
                if (verificationStatus && verificationStatus.verified) {
                  console.log('✅ User verified in database - saving to localStorage and proceeding');
                  // Save to localStorage for future fast access
                  localStorage.setItem('tolbert_age_verification_complete', 'true');
                } else {
                  console.log('❌ User not verified - showing age verification modal');
                  console.log('🔍 DEBUG: Setting showAgeVerification to true');
                  setShowAgeVerification(true);
                  setIsVerifying(true);
                  setLoading(false);
                  return;
                }
              } catch (error) {
                console.error('Database verification check failed:', error);
                console.log('❌ Database check failed - showing verification modal as fallback');
                console.log('🔍 DEBUG: Setting showAgeVerification to true (fallback)');
                setShowAgeVerification(true);
                setIsVerifying(true);
                setLoading(false);
                return;
              }
            } else {
              // If explicitly disabled via env, still protect mobile by checking URL return and localStorage
              console.log('🔍 DEBUG: Age verification explicitly disabled via env');
            }
            // ===== END STRIPE AGE VERIFICATION (DISABLED) =====
            */
      
      if (paymentMethod === 'stripe') {
        // AI CONTEXT: payments - Stripe test mode checkout
        // Reasoning: Use server-created session for secure payment; client redirects to Stripe
        if (!isApiConfigured()) {
          toast.error('Stripe checkout is not configured for this environment. Choose a different payment method.');
          return;
        }
        
        // Use forced cart if provided (for auto-proceed after verification)
        const rawCart = forceCart || cart;
        console.log('handleCheckout - forceCart:', forceCart);
        console.log('handleCheckout - cart state:', cart);
        console.log('handleCheckout - rawCart:', rawCart);
        console.log('handleCheckout - rawCart length:', rawCart?.length);
        console.log('handleCheckout - rawCart type:', typeof rawCart);
        console.log('handleCheckout - isArray:', Array.isArray(rawCart));
        
        // Check if cart has items
        if (!rawCart || rawCart.length === 0) {
          console.error('Cart is empty, cannot proceed with checkout');
          console.error('forceCart:', forceCart);
          console.error('cart state:', cart);
          toast.error('Your cart is empty. Please add items before checkout.');
          return;
        }
        
        // AI CONTEXT: payments - Apply subscription detection guardrail to all cart items
        // Journal: J-20260111-01
        const currentCart = rawCart.map(item => detectAndCorrectSubscriptionItem(item));
        console.log('Cart items for checkout (after guardrail):', currentCart);
        
        // Build items and apply discount if present
        const baseItems = currentCart.map(item => {
          const creditsPerItem = Number(item.credits) > 0 ? Number(item.credits) : (Number(item.entries) > 0 ? Number(item.entries) : 1);
          return {
            name: item.name,
            price: getEffectivePrice(item),  // AI CONTEXT: sales-pricing - Use effective price for checkout
            quantity: item.quantity,
            tier: item.tier,
            credits: creditsPerItem,
            interval: item.isSubscription ? (item.interval || 'month') : undefined,
            // Flag admin-only test products so backend can auto-refund live Stripe charges
            adminTest: item.adminOnly === true,
            shippingCost: item.shippingCost !== undefined ? Number(item.shippingCost) : 5,
          };
        });
        let items = baseItems;
        if (appliedDiscount) {
          const subtotal = baseItems.reduce((s, it) => s + (it.price * it.quantity), 0);
          if (subtotal > 0) {
            if (appliedDiscount.kind === 'percent') {
              const pct = Math.max(0, Math.min(100, Number(appliedDiscount.amount || 0)));
              items = baseItems.map(it => ({
                ...it,
                price: Math.max(0, +(it.price * (1 - pct / 100)).toFixed(2))
              }));
            } else if (appliedDiscount.kind === 'flat') {
              let remaining = Math.min(Number(appliedDiscount.amount || 0), subtotal);
              items = baseItems.map((it, idx) => {
                const lineSubtotal = it.price * it.quantity;
                if (remaining <= 0 || lineSubtotal <= 0) return it;
                const share = idx === baseItems.length - 1
                  ? remaining
                  : Math.min(remaining, Math.round((lineSubtotal / subtotal) * Number(appliedDiscount.amount || 0) * 100) / 100);
                const perUnitOff = Math.min(it.price, +(share / it.quantity).toFixed(2));
                remaining = +(remaining - perUnitOff * it.quantity).toFixed(2);
                return { ...it, price: Math.max(0, +(it.price - perUnitOff).toFixed(2)) };
              });
            }
          }
        }
        // Append Merchant Platform Fee as its own line item for Stripe
        // Compute MPF from subtotal minus discount (pre-tax), rounded up to whole dollar if cents present
        const subtotalForMpf = currentCart.reduce((s, it) => s + (getEffectivePrice(it) * it.quantity), 0);  // AI CONTEXT: sales-pricing - Use effective price for MPF calculation
        let discountForMpf = 0;
        if (appliedDiscount) {
          if (appliedDiscount.kind === 'percent') {
            const pct = Math.max(0, Math.min(100, Number(appliedDiscount.amount || 0)));
            discountForMpf = +(subtotalForMpf * (pct / 100)).toFixed(2);
          } else if (appliedDiscount.kind === 'flat') {
            discountForMpf = Math.min(Number(appliedDiscount.amount || 0), subtotalForMpf);
          }
        }
        const baseForMpf = Math.max(0, subtotalForMpf - discountForMpf);
        const rawMpf = baseForMpf * 0.05;
        const mpfDollars = Math.floor(rawMpf);
        const mpfHasCents = +((rawMpf - mpfDollars).toFixed(2)) > 0;
        const mpfAmount = mpfHasCents ? (mpfDollars + 1) : mpfDollars;
        if (mpfAmount > 0) {
          items = [
            ...items,
            {
              name: 'Merchant Platform Fee',
              price: mpfAmount,
              quantity: 1,
              tier: undefined,
            }
          ];
        }
        const taxAmount = computeTax();
        if (taxAmount > 0) {
          items = [
            ...items,
            {
              name: 'Tax',
              price: Math.round(taxAmount * 100) / 100,
              quantity: 1,
              tier: undefined,
            }
          ];
        }
        let session;
        try {
          // AI CONTEXT: payments - Separate subscription items from one-time items with detailed logging
          // Journal: J-20260111-01
          console.log('🔍 SUBSCRIPTION CHECK: Analyzing cart items...');
          currentCart.forEach((item, idx) => {
            console.log(`  Item ${idx + 1}: "${item.name}"`, {
              isSubscription: item.isSubscription,
              interval: item.interval,
              tier: item.tier,
              price: item.price,
              credits: Number(item.credits) > 0 ? Number(item.credits) : 1
            });
          });
          
          const subscriptionItems = currentCart.filter(i => i.isSubscription);
          const oneTimeItems = currentCart.filter(i => !i.isSubscription);
          
          console.log(`🔍 SUBSCRIPTION CHECK: Found ${subscriptionItems.length} subscription items and ${oneTimeItems.length} one-time items`);
          if (subscriptionItems.length > 0) {
            console.log('  Subscription items:', subscriptionItems.map(i => i.name));
          }
          if (oneTimeItems.length > 0) {
            console.log('  One-time items:', oneTimeItems.map(i => i.name));
          }

          // AI CONTEXT: age-verification - Check for simple age confirmation (new flow) OR Stripe verification (old flow)
          const simpleAgeConfirmed = localStorage.getItem('tolbert_simple_age_confirmed');
          const stripeVerification = localStorage.getItem('tolbert_age_verification_complete');
          const localStorageVerification = simpleAgeConfirmed === 'true' || stripeVerification === 'true';
          
          // Debug logging for age verification
          console.log('🔍 Age verification check:');
          console.log('  - simpleAgeConfirmed:', simpleAgeConfirmed);
          console.log('  - stripeVerification:', stripeVerification);
          console.log('  - localStorageVerification:', localStorageVerification);
          
          const mpfClientShare = +(mpfAmount * 0.70).toFixed(2); // 3.5% of 5%
          const mpfPlatformShare = +(mpfAmount - mpfClientShare).toFixed(2);

          // AI CONTEXT: payments - Prevent mixed cart checkout to avoid subscription loss
          // Journal: J-20260112-01
          // Reasoning: Stripe doesn't support subscription + one-time items in single session
          // This prevents customers from losing their subscription when mixing cart items
          if (subscriptionItems.length > 0 && oneTimeItems.length > 0) {
            console.error('❌ MIXED CART DETECTED: Cannot mix subscriptions with one-time purchases');
            console.error('  Subscription items:', subscriptionItems.map(i => i.name));
            console.error('  One-time items:', oneTimeItems.map(i => i.name));
            
            toast.error(
              'Cannot checkout with both subscriptions and one-time items together. Please remove either the subscription or the one-time items and checkout separately.',
              { duration: 8000 }
            );
            setLoading(false);
            return;
          } else {
            // All items are the same type - proceed with current logic
            const isSubscriptionCart = subscriptionItems.length > 0;
            const checkoutData = {
              items,
              subscription: isSubscriptionCart,
              localStorageVerified: localStorageVerification,
              mpf: {
                amount: mpfAmount,
                clientShare: mpfClientShare,
                platformShare: mpfPlatformShare,
              }
            };
            console.log('🔍 CHECKOUT SESSION: Creating session with data:', {
              isSubscriptionCart,
              subscription: checkoutData.subscription,
              itemCount: items.length,
              items: items.map(i => ({
                name: i.name,
                price: i.price,
                interval: i.interval,
                tier: i.tier,
                credits: i.credits
              }))
            });
            console.log('🔍 CHECKOUT SESSION: Full checkout data:', checkoutData);
            console.log('🔍 CHECKOUT SESSION: localStorageVerified value:', localStorageVerification);
            console.log('🔍 CHECKOUT SESSION: About to call createCheckoutSession...');
            session = await createCheckoutSession(checkoutData);
            console.log('🔍 CHECKOUT SESSION: Session created:', session);
          }

          // AI CONTEXT: ux - Save notification consent before redirecting to Stripe
          // Reasoning: Save consent immediately when user checks boxes, before payment redirect
          // This ensures consent is saved even if user doesn't complete payment
          if (formData.smsConsent && !hasSmsConsent) {
            try {
              const phoneE164 = getUserPhoneE164(user);
              if (phoneE164) {
                await handleCheckoutSmsConsent({ phone: phoneE164, smsConsent: true });
                // Also update profile to mark SMS consent as given
                await updateMyProfile({ smsConsent: true });
                setHasSmsConsent(true); // Update local state to hide checkbox on next visit
                console.log('✅ SMS consent saved during checkout');
              } else {
                console.warn('⚠️ Cannot save SMS consent: no phone number on user profile');
              }
            } catch (error) {
              console.error('❌ Failed to save SMS consent during checkout:', error);
              // Non-fatal - don't block checkout
            }
          }

          // Push notifications disabled - no consent saving needed

          console.log('createCheckoutSession response:', session);
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('createCheckoutSession failed', err);
          }
          
          // Check if it's an age verification error (DISABLED - Stripe verification)
          /*
          if (err.message && err.message.includes('Age verification required')) {
            console.log('Backend requires age verification, showing verification modal');
            setShowAgeVerification(true);
            setIsVerifying(true);
            setLoading(false);
            return;
          }
          */
          
          toast.error('Unable to start checkout. Please try again.');
          return;
        }
        // Prefer server-provided URL for immediate redirect (robust across envs)
        console.log('Session received:', session);
        console.log('Session URL:', session?.url);
        if (session?.url) {
          console.log('Redirecting to Stripe checkout URL:', session.url);
          // Ensure we have a success parameter for post-payment UX
          const url = new URL(session.url);
          if (!url.searchParams.has('success_url') && !url.searchParams.has('session_id')) {
            // Stripe already embeds the session link; just navigate
            window.location.assign(session.url);
          } else {
            window.location.assign(session.url);
          }
          return;
        }

        const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
        if (!publishableKey) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('Missing REACT_APP_STRIPE_PUBLISHABLE_KEY and no session.url');
          }
          toast.error('Payment not configured.');
          return;
        }

        const stripe = await loadStripe(publishableKey);
        if (!stripe || !session?.id) {
          // No session.url at this point
          throw new Error('Stripe not initialized');
        }
        const redirectResult = await stripe.redirectToCheckout({ sessionId: session.id });
        if (redirectResult?.error) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('redirectToCheckout error', redirectResult.error);
          }
          toast.error('Redirect failed. Try again.');
        }
        return;
      }
      const result = await checkout(paymentMethod, user);
      
      if (result.success) {
        // Add purchases to user history
        result.purchases.forEach(purchase => {
          addPurchase(purchase);
          
          // Add credits for each product purchased
          if (purchase.tier) {
            const creditsPerItem = Number(purchase.credits) > 0 ? Number(purchase.credits) : 1;
            addCredits(user.id, purchase.tier, purchase.quantity * creditsPerItem);
          }
        });

        // AI CONTEXT: architecture - Persist credits server-side post-checkout
        // Reasoning: Keep DynamoDB as source of truth for credits awarded
        // Constraints: Non-fatal if API not configured or call fails
        try {
          const items = result.purchases.map(p => ({
            tier: p.tier,
            quantity: p.quantity * (Number(p.credits) > 0 ? Number(p.credits) : 1)
          }));
          console.log('🔍 Checkout: Awarding purchase credits for items:', items);
          const creditResult = await creditService.awardPurchaseCredits(items);
          console.log('🔍 Checkout: Credit award result:', creditResult);
          
          if (creditResult && !creditResult.skipped) {
            toast.success('Credits awarded successfully!');
          } else if (creditResult && creditResult.skipped) {
            console.warn('🔍 Checkout: Credit award skipped (API not configured)');
          }
        } catch (e) {
          // Non-fatal in MVP but log for debugging
          console.error('❌ Checkout: Award purchase credits (API) failed:', e?.message || e);
          toast.error('Warning: Credits may not have been awarded. Please contact support if needed.');
        }
        
        // Show subscription upsell if user is not subscribed
        if (!isUserSubscribed(user.id)) {
          setShowSubscriptionUpsell(true);
        } else {
          // Show bonus entries offer
          setShowBonusEntries(true);
        }
        
        toast.success('Purchase completed successfully!');
      } else {
        toast.error('Payment failed. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred during checkout.');
    } finally {
      setLoading(false);
      // Reset verification flag after checkout process completes (DISABLED - Stripe verification)
      // setReturningFromVerification(false);
    }
          }, [paymentMethod, cart, appliedDiscount, user, addPurchase, addCredits, isUserSubscribed, checkout]);
  
  // Auto-restore cart from localStorage on component mount if returning from age verification
  useEffect(() => {
    // Prevent multiple executions
    if (autoProceeding) {
      console.log('🛑 Auto-proceed already in progress, skipping...');
      return;
    }
    
    // Check if we're returning from age verification (URL has verification=complete)
    const urlParams = new URLSearchParams(window.location.search);
    const verificationParam = urlParams.get('verification');
    
    // DISABLED - Stripe verification auto-restore flow
    /*
    if (verificationParam === 'complete') {
      console.log('Detected return from age verification - auto-restoring cart');
      console.log('Setting returningFromVerification = true');
      setReturningFromVerification(true); // Set flag to bypass verification check
      
      // Save verification success to localStorage as persistent record (one-time verification per customer)
      localStorage.setItem('tolbert_age_verification_complete', 'true');
      console.log('Saved age verification success to localStorage as persistent record');
    */
    
    // Since we're using simple age confirmation now, we don't need this Stripe verification flow
    if (false && verificationParam === 'complete') { // Intentionally disabled
      
      // Try to restore cart multiple times with delays
      const attemptRestore = (attempt = 1, maxAttempts = 3) => {
        const restored = restoreCartFromStorage();
        console.log(`Auto-restore attempt ${attempt} result:`, restored);
        
        if (!restored && attempt < maxAttempts) {
          console.log(`Restore failed, retrying in ${attempt * 500}ms...`);
          setTimeout(() => attemptRestore(attempt + 1, maxAttempts), attempt * 500);
        } else {
          // Clean up URL parameter to prevent loop
          const newUrlParams = new URLSearchParams(window.location.search);
          newUrlParams.delete('verification');
          const newUrl = window.location.pathname + (newUrlParams.toString() ? `?${newUrlParams.toString()}` : '');
          window.history.replaceState({}, '', newUrl);
          console.log('Cleaned up verification URL parameter');
          
          if (restored) {
            console.log('Cart restored successfully, auto-proceeding to payment...');
            setAutoProceeding(true);
            // Auto-proceed to payment after successful cart restoration
            setTimeout(() => {
              console.log('Auto-proceeding to payment - calling handleCheckout()');
              console.log('Current cart state before handleCheckout:', cart);
              console.log('Current cart length:', cart.length);
              
              // Get cart data directly from localStorage to ensure we have it
              const restoredCartData = JSON.parse(localStorage.getItem('tolbert_cart') || '[]');
              console.log('Restored cart data from localStorage:', restoredCartData);
              console.log('Restored cart data length:', restoredCartData.length);
              
              // Pass the restored cart data directly to handleCheckout
              handleCheckout(restoredCartData);
            }, 1000); // Small delay to ensure cart state is updated
          } else {
            console.error('Failed to restore cart after all attempts');
          }
        }
      };
      
      attemptRestore();
    }
  }, [restoreCartFromStorage, handleCheckout, autoProceeding, cart]);

  // Subtle ambient background burst loop
  useEffect(() => {
    const id = setInterval(() => setAmbientTick((n) => n + 1), 9000);
    return () => clearInterval(id);
  }, []);

  // Removed duplicate useEffect that was causing infinite loops

  

  // If redirected back from Stripe
  const params = new URLSearchParams(window.location.search);
  const statusParam = params.get('status');
  const sessionIdParam = params.get('session_id');
  const verificationParam = params.get('verification');
  const pendingEntry = readPendingGiveawayEntry();
  
  console.log('URL parameters:', {
    status: statusParam,
    session_id: sessionIdParam,
    verification: verificationParam,
    allParams: Object.fromEntries(params.entries())
  });

  useEffect(() => {
    if (statusParam !== 'success' || verificationParam) return;
    if (!pendingEntry?.giveawayId) return;
    const timer = setTimeout(() => {
      navigate('/giveaways?autoEnter=1');
    }, 1200);
    return () => clearTimeout(timer);
  }, [statusParam, verificationParam, pendingEntry?.giveawayId, navigate]);

  // AI CONTEXT: ux - Handle authentication check
  // Reasoning: Ensure user is authenticated before checkout
  // Constraints: Must redirect to login if not authenticated
  // Future: Consider implementing guest checkout

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="h-16 w-16 text-theme-silver mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-silver mb-2">Please Sign In</h2>
          <p className="text-theme-silver-light mb-4">You need to be signed in to complete your purchase.</p>
          <button onClick={() => navigate('/login')} className="btn-primary">Sign In</button>
        </div>
      </div>
    );
  }

  // AI CONTEXT: ux - Handle empty cart state
  // Reasoning: Provide clear feedback when cart is empty
  // Constraints: Must guide user back to shopping
  // Future: Consider implementing cart recovery

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="h-16 w-16 text-theme-silver mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-silver mb-2">Your Cart is Empty</h2>
          <p className="text-theme-silver-light mb-4">Add some products to your cart to continue.</p>
          <button onClick={() => navigate('/store')} className="btn-primary">Continue Shopping</button>
        </div>
      </div>
    );
  }



  // AI CONTEXT: business-logic - Handle subscription creation
  // Reasoning: Convert one-time buyers to subscribers
  // Constraints: Must handle subscription creation errors
  // Future: Implement real subscription processing

  const handleSubscribe = async () => {
    setSubscriptionLoading(true);
    
    try {
      const result = await createSubscription(user.id);
      
      if (result.success) {
        toast.success('Subscription activated! You now have early access to giveaways.');
        setShowSubscriptionUpsell(false);
        setShowBonusEntries(true);
      } else {
        toast.error('Failed to activate subscription. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred while activating subscription.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // AI CONTEXT: ux - Handle bonus entries completion
  // Reasoning: Complete the purchase flow and clear cart
  // Constraints: Must provide clear user feedback
  // Future: Implement order confirmation page

  const handleBonusEntries = () => {
    toast.success('Bonus entries added to your account!');
    setShowBonusEntries(false);
    clearCartAfterPurchase();
  };

  // AI CONTEXT: ux - Format currency display
  // Reasoning: Provide consistent currency formatting
  // Constraints: Must handle various currency amounts
  // Future: Consider international currency support

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // AI CONTEXT: business-logic - Calculate earned credits
  // Reasoning: Track credits earned from purchase
  // Constraints: Must match product tier to credit type
  // Future: Implement credit expiration system

  const getCreditsEarned = () => {
    const credits = {};
    cart.forEach(item => {
      const creditsPerItem = Number(item.credits) > 0 ? Number(item.credits) : (Number(item.entries) > 0 ? Number(item.entries) : 1);
      const tier = (item.tier || '').toLowerCase();
      credits[tier] = (credits[tier] || 0) + (item.quantity * creditsPerItem);
    });
    return credits;
  };

  const creditsEarned = getCreditsEarned();

  const computeSubtotal = () => getCartTotal();
  const computeDiscountAmount = () => {
    if (!appliedDiscount) return 0;
    const subtotal = computeSubtotal();
    if (appliedDiscount.kind === 'flat') {
      return Math.min(appliedDiscount.amount, subtotal);
    }
    if (appliedDiscount.kind === 'percent') {
      return Math.max(0, Math.min(subtotal, (subtotal * (appliedDiscount.amount / 100))));
    }
    return 0;
  };
  // AI CONTEXT: shipping - Single highest shipping cost per order
  // Reasoning: Charge shipping once per order using the maximum per‑item shipping cost,
  // rather than summing shipping for every item/quantity in the cart.
  const computeShipping = () => {
    if (!cart || cart.length === 0) return 0;
    let maxShipping = 0;
    for (const item of cart) {
      const raw = item.shippingCost !== undefined ? Number(item.shippingCost) : 5;
      const value = Number.isFinite(raw) ? Math.max(0, raw) : 0;
      if (value > maxShipping) {
        maxShipping = value;
      }
    }
    return maxShipping;
  };
  const computeTax = () => {
    // AI CONTEXT: tax-calculation - manual tax calculator fallback
    // Reasoning: Use manual tax calculation as fallback while Stripe automatic tax is being configured
    // This provides immediate tax calculation until account-level setup is complete
    const subtotal = computeSubtotal();
    const discount = computeDiscountAmount();
    const shipping = computeShipping();
    const taxable = Math.max(0, subtotal - discount + shipping);
    
    // For now, use a default state (UT) - in a real implementation, 
    // you would get the customer's state from their address
    // TODO: Get customer state from address input or user profile
    const customerState = 'UT'; // Default to Utah - replace with actual customer state
    return calculateTax(taxable, customerState);
  };
  // NOTE: This implementation is defined above to satisfy hook dependency ordering
  const computeTotal = () => {
    // AI CONTEXT: tax-calculation - manual tax calculator fallback
    // Reasoning: Include manual tax calculation in total while Stripe automatic tax is being configured
    const subtotal = computeSubtotal();
    const discount = computeDiscountAmount();
    const shipping = computeShipping();
    const tax = computeTax();
    const mpf = computeMerchantPlatformFee();
    return Math.max(0, subtotal - discount + shipping) + tax + mpf;
  };

  const handleApplyCode = async () => {
    const raw = (promoInput || '').trim();
    if (!raw) return;
    if (!isApiConfigured()) {
      toast.error('Code validation not available in this environment');
      return;
    }
    try {
      const res = await validateCode(raw);
      if (res.type === 'coupon') {
        setAppliedDiscount({ ...res.discount, code: res.code });
        setAppliedReferral(null);
        toast.success('Coupon applied');
      } else if (res.type === 'referral') {
        setAppliedReferral({ code: res.code, ownerEmail: res.ownerEmail });
        setAppliedDiscount(null);
        toast.success('Referral recorded. Thanks for supporting!');
      } else {
        toast.error('Code not found');
      }
    } catch (e) {
      toast.error('Unable to validate code');
    }
  };

  // Only clear cart and show success if this is a real payment success (not age verification)
  if (statusParam === 'success' && !verificationParam) {
    console.log('Payment success detected - clearing cart');
    // Attempt to verify and persist purchase server-side (fallback if webhook not set yet)
    if (sessionIdParam) {
      verifyCheckoutSession(sessionIdParam).catch(() => {});
    }
    // Clear cart on confirmed success screen load
    clearCartAfterPurchase();
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-center glass-card-dark p-8">
          <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-silver mb-2">{t('checkout.paymentSuccessful')}</h2>
          <p className="text-theme-silver-light mb-6">{t('checkout.thankYou')}</p>
          {pendingEntry?.giveawayId && (
            <div className="mb-6">
              <p className="text-sm text-theme-silver-light mb-3">
                {pendingEntry.giveawayTitle
                  ? `Ready to finish your entry for ${pendingEntry.giveawayTitle} once your credits post.`
                  : 'Finishing your giveaway entry now that your credits are posted.'}
              </p>
              <button onClick={() => navigate('/giveaways?autoEnter=1')} className="btn-primary">
                Finish Giveaway Entry
              </button>
            </div>
          )}
          {sessionIdParam && (
            <p className="text-xs text-theme-silver-light mb-4">Reference: {sessionIdParam}</p>
          )}
          <button onClick={() => navigate('/store')} className="btn-primary">{t('checkout.continueShopping')}</button>
        </div>
      </div>
    );
  }
  if (statusParam === 'cancelled' || statusParam === 'failed') {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-center glass-card-dark p-8">
          <Lock className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-silver mb-2">{statusParam === 'cancelled' ? t('checkout.paymentCancelled') : t('checkout.paymentFailed')}</h2>
          <p className="text-theme-silver-light mb-6">{t('checkout.noCharges')}</p>
          <button onClick={() => navigate('/checkout')} className="btn-primary">{t('checkout.tryAgain')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-black relative overflow-hidden">
      {/* Ambient faint background bursts */}
      <BurstBags triggerKey={ambientTick} className="absolute inset-0 opacity-30" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-theme-silver">{t('checkout.title')}</h1>
          {/* Test button removed - Stripe verification disabled */}
        </div>

        {/* Auto-proceeding indicator */}
        {autoProceeding && (
          <div className="mb-6 glass-card-dark bg-green-500/20 border-green-400/30 p-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-400"></div>
              <div>
                <h3 className="font-semibold text-theme-silver">Age Verification Complete!</h3>
                <p className="text-sm text-theme-silver-light">
                  Cart restored successfully. Proceeding to payment...
                </p>
              </div>
            </div>
          </div>
        )}



        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="glass-card-dark mb-6 p-6">
              <h2 className="text-xl font-semibold text-theme-silver mb-4">{t('checkout.orderSummary')}</h2>
              
              <div className="space-y-4">
                {cart.map((item) => {
                  const effectivePrice = getEffectivePrice(item);
                  const itemShipping = (item.shippingCost !== undefined ? Number(item.shippingCost) : 5) * (item.quantity || 1);
                  return (
                    <div key={item.id} className="flex items-center justify-between p-4 bg-glass-white/5 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-glass-white/10 rounded-lg flex items-center justify-center overflow-hidden">
                          <S3Image
                            s3KeyOrUrl={item.image || '/files/Logo.png'}
                            alt={item.name}
                            className="w-full h-full object-contain p-1"
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold text-theme-silver">{item.name}</h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-sm text-theme-silver-light">Qty:</span>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="w-6 h-6 bg-glass-white/10 hover:bg-glass-white/20 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={item.quantity <= 1}
                                aria-label={`Decrease quantity of ${item.name}`}
                                title={`Decrease quantity of ${item.name}`}
                              >
                                <Minus className="w-3 h-3 text-theme-silver" />
                              </button>
                              <span className="text-sm text-theme-silver-light min-w-[20px] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="w-6 h-6 bg-glass-white/10 hover:bg-glass-white/20 rounded-full flex items-center justify-center transition-colors"
                                aria-label={`Increase quantity of ${item.name}`}
                                title={`Increase quantity of ${item.name}`}
                              >
                                <Plus className="w-3 h-3 text-theme-silver" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`tier-badge tier-${item.tier.toLowerCase()}`}>
                              {item.tier}
                            </span>
                            <span className="text-sm text-custom-orange">
                              {(Number(item.credits) > 0 ? Number(item.credits) : (Number(item.entries) > 0 ? Number(item.entries) : 1)) * item.quantity} credits
                            </span>
                          </div>
                          {item.salesPrice && (
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs text-custom-orange line-through">${item.price}</span>
                              <span className="text-xs text-theme-silver">${item.salesPrice}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-theme-silver">
                          {formatCurrency(effectivePrice * item.quantity)}
                        </p>
                        {itemShipping > 0 && (
                          <p className="text-xs text-theme-silver-light mt-0.5">
                            Shipping: {formatCurrency(itemShipping)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Totals */}
              <div className="border-t border-theme-silver/20 mt-6 pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-theme-silver-light">{t('checkout.subtotal')}</span>
                    <span className="font-semibold text-theme-silver">{formatCurrency(computeSubtotal())}</span>
                  </div>
                  {appliedDiscount && (
                    <div className="flex justify-between">
                      <span className="text-theme-silver-light">{t('checkout.discount')} ({appliedDiscount.code}):</span>
                      <span className="font-semibold text-green-400">- {formatCurrency(computeDiscountAmount())}</span>
                    </div>
                  )}
                  {computeShipping() > 0 && (
                    <div className="flex justify-between">
                      <span className="text-theme-silver-light">Shipping</span>
                      <span className="font-semibold text-theme-silver">{formatCurrency(computeShipping())}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-theme-silver-light">{t('checkout.tax')} ({getTaxRatePercentage('UT')})</span>
                    <span className="font-semibold text-theme-silver">{formatCurrency(computeTax())}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-theme-silver-light">Merchant Platform Fee</span>
                    <span className="font-semibold text-theme-silver">{formatCurrency(computeMerchantPlatformFee())}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-theme-silver border-t border-theme-silver/20 pt-2">
                    <span>{t('checkout.total')}</span>
                    <span>{formatCurrency(computeTotal())}</span>
                  </div>
                </div>
              </div>

              {/* Credits Earned */}
              <div className="mt-6 p-4 bg-glass-copper/20 border-custom-orange/30 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Gift className="h-5 w-5 text-custom-orange" />
                  <span className="font-semibold text-theme-silver">Credits Earned</span>
                </div>
                <div className="space-y-1">
                  {Object.entries(creditsEarned).map(([tier, count]) => (
                    <p key={tier} className="text-sm text-theme-silver-light">
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}: {count} credit{count > 1 ? 's' : ''}
                    </p>
                  ))}
                </div>
                
                {/* Total Entries Summary */}
                <div className="border-t border-custom-orange/30 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-theme-silver-light">Total Entries:</span>
                    <span className="font-bold text-custom-orange">{getTotalEntries()}</span>
                  </div>
                  <p className="text-xs text-theme-silver-light mt-1">
                    Earn {getTotalEntries()} giveaway entry{getTotalEntries() !== 1 ? 's' : ''} with this purchase
                  </p>
                </div>
                
                <p className="text-xs text-theme-silver-light mt-2">
                  Use these credits to enter giveaways of the same tier level.
                </p>
              </div>
            </div>

            {/* Payment Method */}
            <div className="glass-card-dark mb-6 p-6">
               <h2 className="text-xl font-semibold text-theme-silver mb-4">{t('checkout.paymentMethod')}</h2>
              
              <div className="space-y-4">
                <label className={`flex items-center space-x-3 p-4 border border-theme-silver/20 rounded-lg ${apiConfigured ? 'cursor-pointer hover:bg-glass-white/10' : 'opacity-60 cursor-not-allowed'}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="stripe"
                    checked={paymentMethod === 'stripe'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    disabled={!apiConfigured}
                    className="text-custom-orange"
                  />
                  <div className="flex items-center space-x-3">
                    <CreditCard className="h-6 w-6 text-theme-silver" />
                    <div>
                      <p className="font-medium text-theme-silver">{t('checkout.creditCard')}</p>
                      <p className="text-sm text-theme-silver-light">{apiConfigured ? t('checkout.securePayment') : t('checkout.notConfigured')}</p>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Promo / Referral Code */}
            <div className="glass-card-dark mb-6 p-6">
               <h2 className="text-xl font-semibold text-theme-silver mb-4">{t('checkout.promoOrReferral')}</h2>
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  placeholder={t('checkout.promoPlaceholder')}
                  className="flex-1 glass-input text-theme-silver placeholder-theme-silver-light"
                />
                <button onClick={handleApplyCode} className="btn-secondary">{t('checkout.apply')}</button>
              </div>
              {appliedDiscount && (
                <p className="text-sm text-green-400 mt-2">Applied {appliedDiscount.kind === 'flat' ? `$${appliedDiscount.amount} off` : `${appliedDiscount.amount}% off`} (code {appliedDiscount.code})</p>
              )}
              {appliedReferral && (
                <p className="text-sm text-theme-silver-light mt-2">Referral recognized (code {appliedReferral.code})</p>
              )}
            </div>

            {/* SMS Consent Section - Only show if user hasn't already consented */}
            {!hasSmsConsent && (
              <div className="border-t border-theme-silver/20 pt-6 mb-6">
                <div className="flex items-center space-x-2 mb-4">
                  <MessageSquare className="h-5 w-5 text-custom-orange" />
                  <h3 className="text-lg font-semibold text-theme-silver">SMS Notifications</h3>
                </div>
                <p className="text-sm text-theme-silver-light mb-4">
                  Stay updated on order confirmations, shipping updates, and exclusive offers
                </p>
                
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="smsConsentCheckout"
                    name="smsConsent"
                    checked={formData.smsConsent}
                    onChange={(e) => setFormData(prev => ({ ...prev, smsConsent: e.target.checked }))}
                    className="mt-1 h-4 w-4 text-custom-orange focus:ring-custom-orange border-theme-silver/20 rounded"
                  />
                  <div className="flex-1">
                    <label htmlFor="smsConsentCheckout" className="text-sm text-theme-silver">
                      I consent to receive SMS messages from Tolbert Tactical, including order confirmations, shipping updates, product alerts, cart reminders, and giveaway notifications. Msg & data rates may apply. Reply STOP to unsubscribe. Up to 4 msgs/month.
                    </label>
                    <p className="text-xs text-theme-silver-light mt-1">
                      <Link to="/legal#privacy" className="text-custom-orange hover:text-custom-orange/80">
                        Privacy Policy
                      </Link>{' '}
                      and{' '}
                      <Link to="/legal#terms" className="text-custom-orange hover:text-custom-orange/80">
                        Terms of Service
                      </Link>{' '}
                      apply. Consent is not required as a condition of purchase.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Push Notification Consent Section - DISABLED */}

            {/* Checkout Button */}
            <button
              onClick={() => handleCheckout()}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? 'Processing...' : t('checkout.completePurchase').replace('{amount}', formatCurrency(computeTotal()))}
            </button>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="glass-card-dark p-6">
              <h3 className="text-lg font-semibold text-theme-silver mb-4">{t('checkout.orderDetails')}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-theme-silver-light">{t('checkout.items')}</span>
                  <span className="font-semibold text-theme-silver">{getCartCount()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-theme-silver-light">Subtotal:</span>
                  <span className="font-semibold text-theme-silver">{formatCurrency(computeSubtotal())}</span>
                </div>
                {appliedDiscount && (
                  <div className="flex justify-between">
                    <span className="text-theme-silver-light">Discount:</span>
                    <span className="font-semibold text-green-400">- {formatCurrency(computeDiscountAmount())}</span>
                  </div>
                )}
                {computeShipping() > 0 && (
                  <div className="flex justify-between">
                    <span className="text-theme-silver-light">Shipping:</span>
                    <span className="font-semibold text-theme-silver">{formatCurrency(computeShipping())}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-theme-silver-light">{t('checkout.tax')} ({getTaxRatePercentage('UT')})</span>
                  <span className="font-semibold text-theme-silver">{formatCurrency(computeTax())}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-theme-silver-light">Merchant Platform Fee</span>
                  <span className="font-semibold text-theme-silver">{formatCurrency(computeMerchantPlatformFee())}</span>
                </div>
                <div className="border-t border-theme-silver/20 pt-3">
                  <div className="flex justify-between text-lg font-bold text-theme-silver">
                    <span>Total:</span>
                    <span>{formatCurrency(computeTotal())}</span>
                  </div>
                </div>
                
                {/* Giveaway Entries Summary */}
                <div className="border-t border-theme-silver/20 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-theme-silver-light">Giveaway Entries:</span>
                    <span className="font-bold text-custom-orange">{getTotalEntries()}</span>
                  </div>
                  <p className="text-xs text-theme-silver-light mt-1">
                    Earn {getTotalEntries()} giveaway entry{getTotalEntries() !== 1 ? 's' : ''} with this purchase
                  </p>
                </div>
              </div>

              <div className="mt-4 text-right">
                <button
                  onClick={clearCart}
                  className="btn-secondary"
                >
                  Clear Cart
                </button>
              </div>
            </div>

            {/* Credits Information */}
            <div className="glass-card-dark bg-glass-copper/20 border-custom-orange/30 p-6">
              <div className="flex items-center space-x-2 mb-3">
                <Gift className="h-5 w-5 text-custom-orange" />
                <h3 className="text-lg font-semibold text-theme-silver">Giveaway Credits</h3>
              </div>
              
              <div className="space-y-2">
                {Object.entries(creditsEarned).map(([tier, count]) => (
                  <div key={tier} className="flex justify-between">
                    <span className="text-theme-silver-light">{tier.charAt(0).toUpperCase() + tier.slice(1)} Credits:</span>
                    <span className="font-bold text-theme-silver">{count}</span>
                  </div>
                ))}
                
                {/* Total Entries Summary */}
                <div className="border-t border-custom-orange/30 pt-2 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-theme-silver-light font-medium">Total Entries Earned:</span>
                    <span className="font-bold text-custom-orange text-lg">{getTotalEntries()}</span>
                  </div>
                  <p className="text-xs text-theme-silver-light mt-1">
                    These entries can be used to enter giveaways of the same tier level.
                  </p>
                </div>
                
                <p className="text-xs text-theme-silver-light mt-2">
                  Credits can be used to enter giveaways of the same tier level.
                </p>
              </div>
            </div>

            {/* Security Notice */}
            <div className="glass-card-dark bg-glass-white/5 border-theme-silver/20 p-6">
              <div className="flex items-center space-x-2 mb-3">
                <Lock className="h-5 w-5 text-theme-silver" />
               <h3 className="text-lg font-semibold text-theme-silver">{t('checkout.secureCheckout')}</h3>
              </div>
              
              <p className="text-sm text-theme-silver-light">
                 {t('checkout.paymentEncrypted')}
              </p>
            </div>
          </div>
        </div>

        {/* Subscription Upsell Modal */}
        {showSubscriptionUpsell && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="glass-card-dark rounded-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Crown className="h-8 w-8 text-custom-orange" />
                  <h3 className="text-xl font-bold text-theme-silver">Upgrade to Premium</h3>
                </div>
                
                <div className="space-y-4 mb-6">
                  <p className="text-theme-silver-light">
                    Get exclusive benefits with our premium subscription:
                  </p>
                  
                  <ul className="space-y-2 text-sm text-theme-silver-light">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                      <span>48-hour early access to new giveaways</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                      <span>Priority customer support</span>
                    </li>
                  </ul>
                  
                  <div className="bg-glass-copper/20 p-4 rounded-lg border-custom-orange/30">
                    <p className="text-center">
                      <span className="text-2xl font-bold text-theme-silver">$24.99</span>
                      <span className="text-theme-silver-light">/month</span>
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowSubscriptionUpsell(false)}
                    className="btn-secondary flex-1"
                  >
                    Maybe Later
                  </button>
                  <button
                    onClick={handleSubscribe}
                    disabled={subscriptionLoading}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:ring-offset-2 focus:ring-offset-theme-black shadow-lg hover:shadow-green-500/20 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {subscriptionLoading ? 'Processing...' : 'Subscribe Now'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bonus Entries Modal */}
        {showBonusEntries && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="glass-card-dark rounded-xl max-w-md w-full">
              <div className="p-6 text-center">
                <Gift className="h-12 w-12 text-custom-orange mx-auto mb-4" />
                
                <h3 className="text-xl font-bold text-theme-silver mb-2">Purchase Complete!</h3>
                <p className="text-theme-silver-light mb-6">
                  Your order has been processed successfully. You've earned giveaway credits!
                </p>
                
                <div className="bg-glass-copper/20 p-4 rounded-lg mb-6 border-custom-orange/30">
                  <p className="text-sm text-theme-silver-light">
                    <strong>Credits earned:</strong> Use these to enter giveaways of the same tier level.
                  </p>
                </div>

                <button
                  onClick={handleBonusEntries}
                  className="btn-primary w-full"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Simple Age Confirmation Modal */}
        {showSimpleAgeConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="glass-card-dark p-6 max-w-md w-full mx-auto">
              {/* Header */}
              <div className="flex items-center justify-center mb-6">
                <Shield className="h-12 w-12 text-custom-orange" />
              </div>
              
              <h2 className="text-2xl font-bold text-theme-silver mb-4 text-center">
                Age Confirmation Required
              </h2>
              
              <div className="mb-6">
                <AlertTriangle className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                <p className="text-theme-silver-light text-center mb-4">
                  You must be 21 years or older to complete this purchase.
                </p>
                <p className="text-sm text-theme-silver-light text-center">
                  By clicking "I am 21 or older", you confirm that you meet the age requirement.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    // Save confirmation to localStorage
                    localStorage.setItem('tolbert_simple_age_confirmed', 'true');
                    setShowSimpleAgeConfirm(false);
                    // Retry checkout (will now pass the age check)
                    handleCheckout();
                  }}
                  className="btn-primary w-full text-lg py-4"
                >
                  I am 21 or older
                </button>
                
                <button
                  onClick={() => {
                    setShowSimpleAgeConfirm(false);
                    setLoading(false);
                    toast.error('You must be 21 or older to complete this purchase');
                  }}
                  className="btn-secondary w-full text-lg py-4"
                >
                  Cancel
                </button>
              </div>

              {/* Legal Notice */}
              <div className="mt-6 pt-4 border-t border-theme-silver/20">
                <p className="text-xs text-theme-silver-light text-center">
                  Age verification is required by law for firearm-related purchases and giveaways.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Age Verification Modal (DISABLED - preserved for future use) */}
        {/* 
        <AgeVerificationModal
          isOpen={showAgeVerification}
          onClose={() => setShowAgeVerification(false)}
          onVerificationComplete={() => {
            setShowAgeVerification(false);
            // Don't retry checkout - user will be redirected to dashboard after verification
          }}
        />
        */}
      </div>
    </div>
  );
};

export default Checkout; 